import React, { useState, useEffect, useRef } from 'react';
import { getAuthToken } from './authToken';
import AssignButton from './AssignButton';
import DictateButton from './DictateButton';
import { useShortcuts } from './useShortcuts';
import OfferPicker from './OfferPicker';
import ShortcutSuggestions from './ShortcutSuggestions';
import LiveAlertBar from './LiveAlertBar';
import axios from 'axios';
import BuyerReturnsButton from './BuyerReturnsButton';
import BuyerShipmentsButton from './BuyerShipmentsButton';
import MMAllegroSetupBanner from './MMAllegroSetupBanner';

const API = '/api';

interface Issue {
  id: string;
  type: string;
  openedDate: string;
  subject: string;
  buyer: { id: string; login: string; };
  currentState: { status: string; chatActive: boolean; };
  chat: { lastMessage: any; messagesCount: number; };
  checkoutForm: { id: string; };
  account_id: string;
  account_name: string;
  language?: string;
  return_requested?: boolean;
  decision_made?: boolean;
  assigned_to?: string | null;
  is_handled?: boolean;
}

interface ChatMessage {
  id: string;
  text: string;
  author: { login: string; role: string; };
  createdAt: string;
  attachments: any[];
}

export default function Issues({ onBack, hideHeader, onIssueSelect, expiredAccounts = [], currentUser = null, onOpenAiOpen = () => {}, onOpenReturns = () => {}, onOpenReturn = (_ref: string) => {}, onOpenShipment = (_o: string, _a: string) => {} }: { onBack: () => void; hideHeader?: boolean; onIssueSelect?: (issue: any) => void; expiredAccounts?: string[]; currentUser?: any; onOpenAiOpen?: () => void; onOpenReturns?: () => void; onOpenReturn?: (ref: string) => void; onOpenShipment?: (orderId: string, accountId: string) => void }) {
  const [showIssueHistory, setShowIssueHistory] = React.useState(false);
  const [issueHistory, setIssueHistory] = React.useState<any[]>([]);
  const [issueHistoryLoading, setIssueHistoryLoading] = React.useState(false);
  const isAccountExpired = (accountName: string) => expiredAccounts.includes(accountName);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [selected, setSelected] = useState<Issue | null>(null);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [issueMeta, setIssueMeta] = useState<{ subject?: string; description?: string; reason?: any; expectations?: any; messagesCount?: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [reply, setReply] = useState('');
  const { showSuggestions: showReplySuggestions, suggestions: replySuggestions, handleChange: handleReplyChange, applyShortcut: applyReplyShortcut, setShowSuggestions: setShowReplySuggestions } = useShortcuts();
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [showOfferPicker, setShowOfferPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState('');
  const [filter, setFilter] = useState<string>(() => {
    const pre = localStorage.getItem('issues_prefill_filter');
    if (pre) { localStorage.removeItem('issues_prefill_filter'); return pre; }
    return 'open';
  });
  const [filterAccount, setFilterAccount] = useState('all');
  const [search, setSearch] = useState<string>(() => {
    const pre = localStorage.getItem('issues_prefill_search');
    if (pre) { localStorage.removeItem('issues_prefill_search'); return pre; }
    return '';
  });
  const [issuePage, setIssuePage] = useState(0);
  const [totalIssues, setTotalIssues] = useState(0);
  const ISSUE_PAGE_SIZE = 50;
  const isMobile = window.innerWidth < 768;
  const [mobileView, setMobileView] = useState<'list'|'detail'>('list');
  const [mobileIssuePage, setMobileIssuePage] = useState(0);
  const [accounts, setAccounts] = useState<{id:string,name:string}[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [allegroBannerClosed, setAllegroBannerClosed] = useState(false);
  const [justReturnedFromAllegro] = useState(() => {
    try { return localStorage.getItem('mm_return_module') === 'issues'; } catch (e) { return false; }
  });
  useEffect(() => {
    if (justReturnedFromAllegro) { try { localStorage.removeItem('mm_return_module'); } catch (e) {} }
  }, [justReturnedFromAllegro]);
  const [aiLoading, setAiLoading] = useState(false);
  const [replyIsManual, setReplyIsManual] = useState(false);
  const [copyToast, setCopyToast] = useState('');
  const [claimAction, setClaimAction] = useState<'return'|'decision'|null>(null);
  const [decisionType, setDecisionType] = useState<string>('ACCEPTED_REPAIR');
  const [decisionJustification, setDecisionJustification] = useState('');
  const [returnType, setReturnType] = useState<'RETURN_REQUIRED_SELLER_LABEL'|'RETURN_REQUIRED_CUSTOM'|'RETURN_NOT_REQUIRED'>('RETURN_REQUIRED_SELLER_LABEL');
  const [returnText, setReturnText] = useState('');
  const [returnAttachments, setReturnAttachments] = useState<{id: string, filename: string}[]>([]);
  const [replyTemplates, setReplyTemplates] = useState<any[]>([]);
  const [decisionTemplates, setDecisionTemplates] = useState<any[]>([]);
  const [claimActionLoading, setClaimActionLoading] = useState(false);
  const [claimActionMsg, setClaimActionMsg] = useState('');
  const [showOrders, setShowOrders] = useState(false);
  const [buyerOrders, setBuyerOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [replyTranslation, setReplyTranslation] = useState('');
  const [attachments, setAttachments] = useState<{id: string, filename: string}[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [translatingReply, setTranslatingReply] = useState(false);
  const [translations, setTranslations] = useState<{[key:string]:string}>({});
  const [showTranslation, setShowTranslation] = useState<{[key:string]:boolean}>({});
  const [issueLang, setIssueLang] = useState('pl');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const issueLockHeartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [lockInfo, setLockInfo] = useState<string | null>(null);
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchIssues(0);
    axios.get(`${API}/allegro/accounts`).then(r => { setAccounts(r.data); setAccountsLoaded(true); }).catch(() => setAccountsLoaded(true));
    axios.get(`${API}/settings/reply-templates?category=return`).then(r => setReplyTemplates(r.data.templates || []));
    axios.get(`${API}/settings/reply-templates?category=decision`).then(r => setDecisionTemplates(r.data.templates || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchIssues = async (page: number = 0, overrideFilter?: string) => {
    setLoading(true);
    try {
      const currentFilter = overrideFilter !== undefined ? overrideFilter : filter;
      let showHandled = '';
      if (currentFilter === 'all') showHandled = 'true';
      else if (currentFilter === 'closed') showHandled = 'only';
      const res = await axios.get(`${API}/allegro/issues?limit=100&offset=0&show_handled=${showHandled}`);
      setIssues(res.data.issues || []);
      setTotalIssues(res.data.total || 0);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const selectIssue = async (issue: Issue) => {
    if (selected && selected.id !== issue.id) {
      axios.post(`${API}/allegro/issues/${selected.id}/unlock`).catch(() => {});
      if (issueLockHeartbeatRef.current) { clearInterval(issueLockHeartbeatRef.current); issueLockHeartbeatRef.current = null; }
    }
    setSelected(issue);
    setLockInfo(null);
    localStorage.setItem('assistant_context', issue.type === 'CLAIM' ? 'issues:claim' : 'issues:dispute');
    if (onIssueSelect) onIssueSelect(issue);
    if (isMobile) setMobileView('detail');
    setChat([]);
    setReply('');
    setSent('');
    // Ustaw jezyk od razu z bazy - nie czekaj na AI
    if (issue.language && issue.language !== 'pl') {
      setIssueLang(issue.language);
    } else {
      setIssueLang('pl');
    }
    // Zablokuj issue dla tego agenta
    if (issueLockHeartbeatRef.current) { clearInterval(issueLockHeartbeatRef.current); issueLockHeartbeatRef.current = null; }
    try {
      await axios.post(`${API}/allegro/issues/${issue.id}/lock`);
      setLockInfo(null);
      issueLockHeartbeatRef.current = setInterval(() => {
        axios.post(`${API}/allegro/issues/${issue.id}/lock`)
          .then(() => setLockInfo(null))
          .catch(() => {});
      }, 3 * 60 * 1000);
    } catch (e: any) {
      if (e.response?.status === 423) {
        setLockInfo(e.response.data.detail);
      }
    }
    setLoadingChat(true);
    try {
      const res = await axios.get(`${API}/allegro/issues/${issue.id}/chat?account_id=${issue.account_id}`);
      const messages = (res.data.chat || []).sort((a: ChatMessage, b: ChatMessage) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setChat(messages);
      setIssueMeta({
        subject: res.data.issueSubject,
        description: res.data.issueDescription,
        reason: res.data.issueReason,
        expectations: res.data.issueExpectations,
        messagesCount: res.data.issueMessagesCount,
      });
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      
      // Wykryj jezyk kupujacego
      const buyerMsgs = messages.filter((m: ChatMessage) => m.author?.role === 'BUYER');
      let issueLangDetected = '';
      if (buyerMsgs.length > 0) {
        const lastText = buyerMsgs[buyerMsgs.length-1].text || '';
        try {
          const langRes = await axios.post(`${API}/ai/detect-language`, { text: lastText });
          issueLangDetected = langRes.data.language || 'pl';
          setIssueLang(issueLangDetected);
        } catch(e) {}
      }
      setTranslations({});
      setShowTranslation({});
      // Wczesniej wykonane tlumaczenia - pokaz od razu. Endpoint czyta WYLACZNIE z cache.
      // Jezyk bierzemy z tej samej zmiennej co przycisk tlumaczenia (issue.language lub wykryty).
      const langNow = (buyerMsgs.length > 0 && issueLangDetected) ? issueLangDetected
                      : ((issue.language && issue.language !== 'pl') ? issue.language : 'pl');
      if (langNow !== 'pl' && messages.length) {
        axios.post(`${API}/ai/translations-cached`, {
          items: messages
            .filter((m: ChatMessage) => m.text)
            .map((m: ChatMessage) => ({ id: m.id, text: m.text, source_lang: langNow })),
        }).then(r => {
          const found = r.data?.translations || {};
          const ids = Object.keys(found);
          if (ids.length) {
            setTranslations(prev => ({ ...prev, ...found }));
            setShowTranslation(prev => {
              const next = { ...prev };
              ids.forEach(id => { next[id] = true; });
              return next;
            });
          }
        }).catch(() => {});
      }
      setReplyTranslation('');
      setClaimAction(null);
      setClaimActionMsg('');
      setDecisionJustification('');
    } catch(e) { console.error(e); }
    setLoadingChat(false);
  };

  const uploadFile = async (file: File) => {
    if (!selected) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('account_id', selected.account_id || '');
      formData.append('file', file);
      const res = await axios.post(`${API}/allegro/upload-issue-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachments(prev => [...prev, { id: res.data.attachment_id, filename: res.data.filename }]);
    } catch (e: any) {
      alert('Błąd uploadu: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setUploadingFile(false);
  };

  const fetchBuyerOrders = async () => {
    if (!selected) return;
    setLoadingOrders(true);
    setShowOrders(true);
    try {
      const res = await axios.get(`${API}/allegro/buyer-orders`, {
        params: { buyer_login: selected.buyer?.login || '', account_id: selected.account_id || '' }
      });
      setBuyerOrders(res.data.orders || []);
    } catch (e) { console.error(e); }
    setLoadingOrders(false);
  };

  const sendMessage = async (learn: boolean) => {
    if (!selected || !reply.trim()) return;
    // Sprawdz polaczenie przed wyslaniem
    try {
      const accountsRes = await axios.get(`${API}/allegro/accounts`);
      const now = new Date();
      const expired = (accountsRes.data || []).filter((a: any) =>
        a.id === selected.account_id && new Date(a.expires_at.replace(' ', 'T') + (a.expires_at.includes('Z') || a.expires_at.includes('+') ? '' : 'Z')) < now
      );
      if (expired.length > 0) {
        alert(`Brak połączenia z kontem ${selected.account_name}. Odśwież połączenie w Ustawieniach.`);
        return;
      }
    } catch(e) {}
    setSending(true);
    try {
      let textToSend = reply;
      if (issueLang !== 'pl') {
        const res = await axios.post(`${API}/ai/translate-from-polish`, { text: reply, target_lang: issueLang });
        textToSend = res.data.translation;
      }
      await axios.post(`${API}/allegro/issues/${selected.id}/message`, {
        text: textToSend,
        account_id: selected.account_id,
          attachment_ids: attachments.map(a => a.id)
      });
      setSent('Wiadomość wysłana!');
        setAttachments([]);
      if (learn && replyIsManual) {
        const clientMsgs = chat.filter(m => ['BUYER', 'ALLEGRO_ADVISOR', 'SYSTEM'].includes(m.author?.role));
        if (clientMsgs.length > 0) {
          const lastClientMsg = clientMsgs[clientMsgs.length-1].text || '';
          await axios.post(`${API}/ai/approve-and-learn`, {
            question: lastClientMsg, answer: reply,
            category: selected.type === 'CLAIM' ? 'reklamacja' : 'dyskusja',
            account_name: selected.account_name || ''
          }).catch(() => {});
        }
      }
      setReply('');
      setTimeout(() => setSent(''), 3000);
      // Czekaj 1s zanim odswiezysz czat
      setTimeout(() => selectIssue(selected), 1000);
    } catch(e: any) {
      setSent('Błąd: ' + (e.response?.data?.detail || e.response?.data?.errors?.[0]?.message || 'nieznany'));
    }
    setSending(false);
  };

  const generateAI = async () => {
    if (!selected || chat.length === 0) return;
    const isClientRole = (role: string) => ['BUYER', 'ALLEGRO_ADVISOR', 'SYSTEM'].includes(role);
    const clientMsgs = chat.filter(m => isClientRole(m.author?.role));
    if (!clientMsgs.length) return;
    const lastMsg = clientMsgs[clientMsgs.length-1].text || '';
    const history = chat.map(m => ({
      text: m.text,
      is_client: isClientRole(m.author?.role)
    }));
    setAiLoading(true);
    try {
      const context = `Typ sprawy: ${selected.type}. Temat: ${selected.subject}. Status: ${selected.currentState?.status}.`;
      const res = await axios.post(`${API}/ai/generate-reply`, {
        message: lastMsg,
        context,
        history,
        account_id: selected.account_id || '',
        hint: reply
      });
      setReply(res.data.draft);
    } catch(e) { console.error(e); }
    setAiLoading(false);
  };

  const formatDate = (d: string) => {
    if (!d) return '';
    const s = d.replace(' ', 'T');
    const date = new Date(!s.includes('Z') && !s.includes('+') ? s + 'Z' : s);
    return date.toLocaleString('pl-PL', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' });
  };

  const statusColors: any = {
    'DISPUTE_OPEN': { bg: '#fef2f2', color: '#dc2626', label: 'Otwarta' },
    'DISPUTE_CLOSED': { bg: '#f0fdf4', color: '#15803d', label: 'Zamknięta' },
    'DISPUTE_ONGOING': { bg: '#fef2f2', color: '#dc2626', label: 'W toku' },
    'DISPUTE_UNRESOLVED': { bg: '#fef9c3', color: '#92400e', label: 'Nierozwiązana' },
    'CLAIM_SUBMITTED': { bg: '#fef2f2', color: '#dc2626', label: 'Złożona' },
    'CLAIM_ACCEPTED': { bg: '#f0fdf4', color: '#15803d', label: 'Zaakceptowana' },
    'CLAIM_REJECTED': { bg: '#f3f4f6', color: '#6b7280', label: 'Odrzucona' },
    'CLAIM_OPEN': { bg: '#fef9c3', color: '#92400e', label: 'Reklamacja otwarta' },
    'CLAIM_CLOSED': { bg: '#f0fdf4', color: '#15803d', label: 'Reklamacja zamknięta' },
  };

  const typeLabels: any = {
    'DISPUTE': '💬 Dyskusja',
    'CLAIM': '⚠️ Reklamacja',
  };

  const translateSubject = (subject: string): string => {
    if (!subject) return '';
    const s = subject.toLowerCase().trim();
    const map: {[key:string]:string} = {
      // Angielskie opisy
      'does not match the description': 'Produkt niezgodny z opisem',
      'the product does not match the description': 'Produkt niezgodny z opisem',
      'defects emerged during use': 'Wady ujawnione podczas użytkowania',
      'the product has a defect that emerged during use': 'Wada ujawniona podczas użytkowania',
      'i have not received the product': 'Nie otrzymano produktu',
      'the product is damaged, but the parcel is intact': 'Produkt uszkodzony, paczka nienaruszona',
      'the product is incomplete': 'Produkt niekompletny',
      'the seller has not paid the return shipping costs': 'Sprzedający nie pokrył kosztów zwrotu',
      'my reason is not on the list': 'Inny powód (spoza listy)',
      'i want to return without giving a reason': 'Zwrot bez podania powodu',
      'the product does not work': 'Produkt nie działa',
      'the product is broken': 'Produkt jest uszkodzony',
      'no refund after returning the product': 'Brak zwrotu po odesłaniu towaru',
      'wrong product was sent': 'Wysłano błędny produkt',
      'product was damaged during delivery': 'Produkt uszkodzony podczas dostawy',
      'product does not work': 'Produkt nie działa',
      'missing parts or accessories': 'Brak części lub akcesoriów',
      'product is counterfeit': 'Produkt jest podróbką',
      'other': 'Inny powód',
      // Kody API (snake_case)
      'product_not_as_described': 'Produkt niezgodny z opisem',
      'defects_emerged_during_use': 'Wady ujawnione podczas użytkowania',
      'defect_found_during_use': 'Wada ujawniona podczas użytkowania',
      'detect_found_during_use': 'Wada ujawniona podczas użytkowania',
      'defect_found_after_use': 'Wada po użytkowaniu',
      'product_not_received': 'Nie otrzymano produktu',
      'product_damaged_parcel_intact': 'Produkt uszkodzony, paczka nienaruszona',
      'product_damaged': 'Produkt uszkodzony',
      'wrong_product': 'Błędny produkt',
      'no_refund_after_returning_product': 'Brak zwrotu po odesłaniu towaru',
      'product_incomplete': 'Produkt niekompletny',
      'cosmetic_damage': 'Uszkodzenie kosmetyczne',
      'dispute_open': 'Dyskusja otwarta',
      'dispute_ongoing': 'Dyskusja w toku',
    };
    const normalized = s.replace(/_/g, ' ');
    return map[s] || map[normalized] || subject;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const subjectLabels: any = {
    'PRODUCT_NOT_RECEIVED': 'Nie otrzymano produktu',
    'PRODUCT_NOT_AS_DESCRIBED': 'Produkt niezgodny z opisem',
    'does not match the description': 'Produkt niezgodny z opisem',
    'PRODUCT_DAMAGED': 'Produkt uszkodzony',
    'WRONG_PRODUCT': 'Błędny produkt',
    'NO_REFUND_AFTER_RETURNING_PRODUCT': 'Brak zwrotu po odesłaniu',
    'DEFECTS_EMERGED_DURING_USE': 'Wady ujawnione podczas użytkowania',
    'defects emerged during use': 'Wady ujawnione podczas użytkowania',
    'DEFECT_FOUND_DURING_USE': 'Wada ujawniona podczas użytkowania',
    'DETECT_FOUND_DURING_USE': 'Wada ujawniona podczas użytkowania',
    'PRODUCT_DAMAGED_PARCEL_INTACT': 'Produkt uszkodzony, paczka nienaruszona',
    'the product is damaged, but the parcel is intact': 'Produkt uszkodzony, paczka nienaruszona',
    'PRODUCT_INCOMPLETE': 'Produkt niekompletny',
    'the product is incomplete': 'Produkt niekompletny',
    'OTHER': 'Inny powód',
    'DEFECT_FOUND_AFTER_USE': 'Wada po użytkowaniu',
    'COSMETIC_DAMAGE': 'Uszkodzenie kosmetyczne',
    'I have not received the product': 'Nie otrzymano produktu',
    'i have not received the product': 'Nie otrzymano produktu',
    'the product does not match the description': 'Produkt niezgodny z opisem',
    'the seller has not paid the return shipping costs': 'Sprzedający nie pokrył kosztów zwrotu',
    'the product has a defect that emerged during use': 'Wada ujawniona podczas użytkowania',
  };

  const filtered = issues.filter(i => {
    if (filterAccount !== 'all' && i.account_id !== filterAccount) return false;
    if (search.trim()) {
      const s = search.trim().replace(/\s+/g, " ").toLowerCase();
      if (!i.buyer?.login?.toLowerCase().includes(s) && !i.subject?.toLowerCase().includes(s)) return false;
    }
    return true;
  });


  // MOBILE - widok listy
  if (isMobile && mobileView === 'list') {
    return (
      <>
        {showOrders && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowOrders(false)}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 560, maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>🛒 Zamówienia: {selected?.buyer?.login}</div>
                <button onClick={() => setShowOrders(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
              </div>
              {loadingOrders && <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Ładowanie...</div>}
              {!loadingOrders && buyerOrders.length === 0 && (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Brak zamówień</div>
              )}
              {!loadingOrders && buyerOrders.map((o, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  {o.image && <img src={o.image} alt={o.offer_name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
                  {!o.image && <div style={{ width: 56, height: 56, background: '#f3f4f6', borderRadius: 6, flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.offer_name}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {o.sku && <span>SKU: {o.sku}</span>}
                      {o.offer_id && <span>ID: {o.offer_id}</span>}
                      <span>Ilość: {o.quantity}</span>
                      {o.bought_at && <span>{new Date(o.bought_at).toLocaleDateString('pl-PL')}</span>}
{(o as any).smart ? <span style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 4, padding: '0 6px', fontWeight: 600 }}>Smart</span> : <span style={{ color: '#9ca3af' }}>bez Smart</span>}
{(o as any).apilo_id && <span title="Kliknij, aby skopiować" onClick={() => { try { navigator.clipboard.writeText(String((o as any).apilo_id)); } catch (e) {} }} style={{ cursor: 'pointer' }}>Apilo: <b style={{ color: '#6d28d9' }}>{(o as any).apilo_id}</b></span>}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                    {o.price} {o.currency}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      {copyToast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1d4ed8', color: '#fff', padding: '8px 18px', borderRadius: 20,
          fontSize: 13, fontWeight: 500, zIndex: 9999, pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          ✓ Skopiowano: {copyToast}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', height: hideHeader ? '100%' : '100dvh', fontFamily: 'system-ui' }}>
        {selected?.type === 'CLAIM' && (
          <div style={{ padding: '8px 16px', background: '#fff', borderBottom: '1px solid #e5e7eb',
            display: 'flex', gap: 8 }}>
            <button
              disabled={selected.return_requested}
              onClick={() => setClaimAction('return')}
              style={{ flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 8,
                border: '1px solid #bfdbfe',
                background: selected.return_requested ? '#f3f4f6' : '#eff6ff',
                color: selected.return_requested ? '#9ca3af' : '#1d4ed8',
                cursor: selected.return_requested ? 'not-allowed' : 'pointer' }}>
              {selected.return_requested ? '✓ Zwrot zażądany' : '📦 Żądaj zwrotu'}
            </button>
            <button
              disabled={selected.decision_made}
              onClick={() => setClaimAction('decision')}
              style={{ flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 8,
                border: '1px solid #fde68a',
                background: selected.decision_made ? '#f3f4f6' : '#fefce8',
                color: selected.decision_made ? '#9ca3af' : '#92400e',
                cursor: selected.decision_made ? 'not-allowed' : 'pointer' }}>
              {selected.decision_made ? '✓ Decyzja wydana' : '⚖️ Decyzja'}
            </button>
          </div>
        )}
        {!hideHeader && (
          <div style={{ background: '#1d4ed8', color: '#fff', padding: '12px 16px' }}>
            <div style={{ fontWeight: 600, fontSize: 16 }}>⚖️ Dyskusje i reklamacje</div>
          </div>
        )}
        <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 6 }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setMobileIssuePage(0); fetchIssues(0); } }}
            placeholder="Szukaj po loginie..."
            style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8,
              fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
          <button onClick={() => { setMobileIssuePage(0); fetchIssues(0); }}
            style={{ padding: '8px 14px', background: '#1d4ed8', color: '#fff', border: 'none',
              borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
            Szukaj
          </button>
          {search && <button onClick={() => { setSearch(''); setMobileIssuePage(0); fetchIssues(0); }}
            style={{ padding: '8px 10px', background: '#f3f4f6', color: '#6b7280', border: 'none',
              borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>✕</button>}
        </div>
        <div style={{ display: 'flex', gap: 4, padding: '6px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
          {[{k:'open',l:'Nowe'},{k:'all',l:'Wszystkie'}].map(f => (
            <button key={f.k} onClick={() => { setFilter(f.k); fetchIssues(0, f.k); setSelectedIssueIds(new Set()); }}
              style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, border: '1px solid #d1d5db',
                background: filter === f.k ? '#1d4ed8' : 'transparent',
                color: filter === f.k ? '#fff' : '#6b7280', cursor: 'pointer', flex: 1 }}>
              {f.l}
            </button>
          ))}
          <AssignButton
            selectedIds={Array.from(selectedIssueIds)}
            endpoint="/api/assign/issues"
            currentUser={currentUser}
            onAssigned={() => { setSelectedIssueIds(new Set()); fetchIssues(0); }}
            size="small"
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', background: '#f9fafb' }}>
          {loading && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>Ładowanie...</div>}
          {filtered.slice(issuePage * ISSUE_PAGE_SIZE, (issuePage + 1) * ISSUE_PAGE_SIZE).map(issue => {
            const statusStyle = statusColors[issue.currentState?.status] || { bg: '#f3f4f6', color: '#6b7280', label: issue.currentState?.status };
            return (
              <div key={issue.id}
                style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb',
                  background: selectedIssueIds.has(issue.id) ? '#fef9c3' : '#fff',
                  borderLeft: issue.currentState?.status?.includes('CLOSED') ? '3px solid #d1d5db' : '3px solid #dc2626',
                  display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={selectedIssueIds.has(issue.id)}
                  onChange={e => { e.stopPropagation(); const next = new Set(selectedIssueIds); if (e.target.checked) next.add(issue.id); else next.delete(issue.id); setSelectedIssueIds(next); }}
                  onClick={e => e.stopPropagation()}
                  style={{ marginTop: 4, flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }} />
                <div style={{ flex: 1, minWidth: 0 }} onClick={() => selectIssue(issue)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>
                    {typeLabels[issue.type] || issue.type} – {issue.language && issue.language !== 'pl' && <img src={`/flags/svg/${issue.language}.svg`} alt={issue.language} width='17' height='13' style={{verticalAlign:'middle',marginRight:2}} />}<span title="Kliknij aby skopiować" onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(issue.buyer?.login || ''); setCopyToast(issue.buyer?.login || ''); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>{issue.buyer?.login}</span>
                  </span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDate(issue.openedDate)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {translateSubject(issue.subject)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 8, background: statusStyle.bg, color: statusStyle.color }}>
                    {statusStyle.label}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {issue.assigned_to && (() => { const agent = (window as any).__agents__?.find((a: any) => a.id === issue.assigned_to); return agent ? <div title={agent.name} style={{ width: 8, height: 8, borderRadius: '50%', background: agent.color || '#9ca3af' }} /> : null; })()}
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{issue.account_name}</span>
                  </div>
                </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', background: '#fff', padding: '6px 12px', borderTop: '1px solid #e5e7eb', alignItems: 'center', gap: 6 }}>
          <button onClick={() => { const p = Math.max(0, mobileIssuePage-1); setMobileIssuePage(p); fetchIssues(p); }}
            disabled={mobileIssuePage === 0}
            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
              background: 'transparent', cursor: mobileIssuePage === 0 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>←</button>
          <span style={{ fontSize: 11, color: '#6b7280', flex: 1, textAlign: 'center' }}>
            {mobileIssuePage+1}/{Math.max(1, Math.ceil(totalIssues/ISSUE_PAGE_SIZE))} · {totalIssues} spraw
          </span>
          <button onClick={() => { const p = mobileIssuePage+1; setMobileIssuePage(p); fetchIssues(p); }}
            disabled={(mobileIssuePage+1)*ISSUE_PAGE_SIZE >= totalIssues}
            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
              background: 'transparent', cursor: (mobileIssuePage+1)*ISSUE_PAGE_SIZE >= totalIssues ? 'not-allowed' : 'pointer', color: '#6b7280' }}>→</button>
        </div>
        {!hideHeader && (
          <div style={{ display: 'flex', background: '#1d4ed8', padding: '8px 0', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <button onClick={onBack} style={{ flex: 1, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11 }}>
              <div style={{ fontSize: 20 }}>✉️</div><div>Skrzynka</div>
            </button>
            <button onClick={() => { setMobileIssuePage(0); fetchIssues(0); }} style={{ flex: 1, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11, borderRadius: 8, margin: '0 8px' }}>
              <div style={{ fontSize: 20 }}>↻</div><div>Odśwież</div>
            </button>
          </div>
        )}
      </div>
    </>
    );
  }

  // MOBILE - widok szczegółów
  if (isMobile && mobileView === 'detail' && selected) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: hideHeader ? '100%' : '100dvh', fontFamily: 'system-ui' }}>
        {/* MODAL MOBILE */}
        {claimAction && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', padding: 20, width: '100%',
              maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 -4px 30px rgba(0,0,0,0.2)' }}>
              {claimAction === 'return' && (
                <>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>📦 Odesłanie towaru</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>Wybierz sposób odesłania towaru.</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                    {[
                      { value: 'RETURN_REQUIRED_SELLER_LABEL', label: '📦 Zaplanuj odesłanie – etykieta od sprzedającego', sub: 'Sprzedający pokrywa koszt zwrotu' },
                      { value: 'RETURN_REQUIRED_CUSTOM', label: '🚚 Zaplanuj odesłanie – własna wysyłka', sub: 'Kupujący pokrywa koszt zwrotu' },
                      { value: 'RETURN_NOT_REQUIRED', label: '✓ Bez odesłania', sub: 'Nie wymagamy zwrotu towaru' },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setReturnType(opt.value as any)}
                        style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', textAlign: 'left',
                          border: '2px solid ' + (returnType === opt.value ? '#1d4ed8' : '#d1d5db'),
                          background: returnType === opt.value ? '#eff6ff' : 'transparent',
                          color: returnType === opt.value ? '#1d4ed8' : '#374151' }}>
                        <div style={{ fontWeight: returnType === opt.value ? 600 : 400 }}>{opt.label}</div>
                        <div style={{ fontSize: 11, opacity: 0.7 }}>{opt.sub}</div>
                      </button>
                    ))}
                    {selected?.type === 'CLAIM' && (
                      <a href={`https://salescenter.allegro.com/claims/${selected.id}`} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'block', padding: '10px 14px', borderRadius: 8, fontSize: 13, textAlign: 'left', textDecoration: 'none',
                          border: '2px dashed #fb923c', background: '#fff7ed', color: '#9a3412' }}>
                        <div style={{ fontWeight: 600 }}>🎁 Bezpłatny zwrot w ramach Allegro Smart! ↗</div>
                        <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>Otwiera tę reklamację w Allegro Sales Center (zaloguj się na konto {selected.account_name}) – tam wybierz „Zaplanuj odesłanie" → „Bezpłatny zwrot w ramach Allegro Smart!". Opcja dostępna tylko dla zamówień Smart!, dopóki nie minął termin na odstąpienie od umowy.</div>
                      </a>
                    )}
                  </div>
                  {replyTemplates.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Wzory odpowiedzi:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {replyTemplates.map(t => (
                          <button key={t.id} onClick={() => setReturnText(t.text)}
                            style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20,
                              border: '1px solid #bfdbfe', background: '#eff6ff',
                              color: '#1d4ed8', cursor: 'pointer' }}>
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <textarea value={returnText} onChange={e => setReturnText(e.target.value)}
                    placeholder="Wiadomość do kupującego (opcjonalnie)" rows={3}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 6,
                      fontSize: 13, fontFamily: 'system-ui', resize: 'none', outline: 'none',
                      boxSizing: 'border-box', marginBottom: 12 }} />
                    {returnType === 'RETURN_REQUIRED_SELLER_LABEL' && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                          📎 Etykieta zwrotna (wymagana gdy sprzedający pokrywa koszt):
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <label style={{ padding: '5px 12px', background: '#f3f4f6', border: '1px solid #d1d5db',
                            borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#374151' }}>
                            📎 {uploadingFile ? 'Uploading...' : 'Załącz etykietę'}
                            <input type="file" style={{ display: 'none' }}
                              accept="image/png,image/jpeg,image/gif,image/bmp,image/tiff,application/pdf"
                              onChange={async e => {
                                if (e.target.files?.[0] && selected) {
                                  setUploadingFile(true);
                                  try {
                                    const formData = new FormData();
                                    formData.append('account_id', selected.account_id || '');
                                    formData.append('file', e.target.files[0]);
                                    const res = await axios.post(`${API}/allegro/upload-attachment`, formData, {
                                      headers: { 'Content-Type': 'multipart/form-data' }
                                    });
                                    setReturnAttachments([{ id: res.data.attachment_id, filename: res.data.filename }]);
                                  } catch (err: any) {
                                    alert('Błąd uploadu: ' + (err.response?.data?.detail || 'nieznany'));
                                  }
                                  setUploadingFile(false);
                                }
                                e.target.value = '';
                              }}
                              disabled={uploadingFile} />
                          </label>
                          {returnAttachments.map((a, idx) => (
                            <span key={idx} style={{ padding: '3px 8px', background: '#dbeafe', borderRadius: 6,
                              fontSize: 12, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                              📄 {a.filename}
                              <button onClick={() => setReturnAttachments([])}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14, padding: 0 }}>✕</button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  {claimActionMsg && <div style={{ fontSize: 12, color: claimActionMsg.includes('Błąd') ? '#dc2626' : '#15803d', marginBottom: 10 }}>{claimActionMsg}</div>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setClaimAction(null); setClaimActionMsg(''); setReturnText(''); }}
                      style={{ flex: 1, padding: '10px', border: '1px solid #d1d5db', background: 'transparent', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Anuluj</button>
                    <button onClick={async () => {
                      setClaimActionLoading(true);
                      try {
                        const res = await axios.post(`${API}/allegro/issues/${selected.id}/request-return`, {
                          account_id: selected.account_id, return_type: returnType, text: returnText, attachment_ids: returnAttachments.map(a => a.id)
                        });
                        if (res.data?.data?.status_code >= 400) {
                          setClaimActionMsg('Błąd: ' + JSON.stringify(res.data?.data));
                        } else {
                          setClaimActionMsg('✓ Wysłano!');
                          setSelected({...selected, return_requested: true});
                          setTimeout(() => { setClaimAction(null); setClaimActionMsg(''); setReturnText(''); fetchIssues(0); }, 1500);
                        }
                      } catch(e: any) { setClaimActionMsg('Błąd: ' + (e.response?.data?.detail || 'nieznany')); }
                      setClaimActionLoading(false);
                    }} disabled={claimActionLoading}
                      style={{ flex: 1, padding: '10px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                      {claimActionLoading ? '⏳' : 'Wyślij'}
                    </button>
                  </div>
                </>
              )}
              {claimAction === 'decision' && (
                <>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>⚖️ Decyzja reklamacyjna</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>Wybierz decyzję i wpisz wiadomość.</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d', marginBottom: 6 }}>Uznanie:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                    {[
                      { value: 'ACCEPTED_REPAIR', label: '🔧 Naprawa towaru' },
                      { value: 'ACCEPTED_EXCHANGE', label: '🔄 Wymiana towaru' },
                      { value: 'ACCEPTED_REFUND', label: '💰 Zwrot pełnej kwoty' },
                      { value: 'ACCEPTED_PARTIAL_REFUND', label: '💸 Zwrot częściowy' },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setDecisionType(opt.value)}
                        style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', textAlign: 'left',
                          border: '2px solid ' + (decisionType === opt.value ? '#15803d' : '#d1d5db'),
                          background: decisionType === opt.value ? '#f0fdf4' : 'transparent',
                          color: decisionType === opt.value ? '#15803d' : '#374151',
                          fontWeight: decisionType === opt.value ? 600 : 400 }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', marginBottom: 6 }}>Odrzucenie:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                    {[
                      { value: 'REJECTED_OTHER', label: '✗ Inny powód' },
                      { value: 'REJECTED_PRODUCT_NOT_RETURNED', label: '✗ Produkt nie odesłany' },
                      { value: 'REJECTED_PRODUCT_DAMAGED_BY_USER', label: '✗ Uszkodzony przez użytkownika' },
                      { value: 'REJECTED_PRODUCT_CONFORMS_TO_CONTRACT', label: '✗ Zgodny z umową' },
                      { value: 'REJECTED_MINOR_DEFECT', label: '✗ Drobna wada' },
                      { value: 'REJECTED_ADDITIONAL_REQUIREMENTS_NOT_COMPLETED', label: '✗ Nie spełniono wymogów' },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setDecisionType(opt.value)}
                        style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', textAlign: 'left',
                          border: '2px solid ' + (decisionType === opt.value ? '#dc2626' : '#d1d5db'),
                          background: decisionType === opt.value ? '#fef2f2' : 'transparent',
                          color: decisionType === opt.value ? '#dc2626' : '#374151',
                          fontWeight: decisionType === opt.value ? 600 : 400 }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {decisionTemplates.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Wzory odpowiedzi:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {decisionTemplates.map(t => (
                          <button key={t.id} onClick={() => setDecisionJustification(t.text)}
                            style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20,
                              border: '1px solid #fde68a', background: '#fefce8',
                              color: '#92400e', cursor: 'pointer' }}>
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <textarea value={decisionJustification} onChange={e => setDecisionJustification(e.target.value)}
                    placeholder={decisionType?.startsWith('ACCEPTED') ? 'Wiadomość (opcjonalna)' : 'Uzasadnienie (wymagane)'}
                    rows={3}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 6,
                      fontSize: 13, fontFamily: 'system-ui', resize: 'none', outline: 'none',
                      boxSizing: 'border-box', marginBottom: 10 }} />
                  {claimActionMsg && <div style={{ fontSize: 12, color: claimActionMsg.includes('Błąd') ? '#dc2626' : '#15803d', marginBottom: 10 }}>{claimActionMsg}</div>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setClaimAction(null); setClaimActionMsg(''); setDecisionJustification(''); }}
                      style={{ flex: 1, padding: '10px', border: '1px solid #d1d5db', background: 'transparent', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Anuluj</button>
                    <button
                      disabled={claimActionLoading || (decisionType?.startsWith('REJECTED') && !decisionJustification.trim())}
                      onClick={async () => {
                        setClaimActionLoading(true);
                        try {
                          const res = await axios.post(`${API}/allegro/issues/${selected.id}/decision`, {
                            account_id: selected.account_id, decision: decisionType, message: decisionJustification
                          });
                          if (res.data?.data?.status_code >= 400) {
                            setClaimActionMsg('Błąd: ' + JSON.stringify(res.data?.data));
                          } else {
                            setClaimActionMsg('✓ Wysłano!');
                            setSelected({...selected, decision_made: true});
                            setTimeout(() => { setClaimAction(null); setClaimActionMsg(''); setDecisionJustification(''); fetchIssues(0); }, 1500);
                          }
                        } catch(e: any) { setClaimActionMsg('Błąd: ' + (e.response?.data?.detail || 'nieznany')); }
                        setClaimActionLoading(false);
                      }}
                      style={{ flex: 1, padding: '10px',
                        background: claimActionLoading || (decisionType?.startsWith('REJECTED') && !decisionJustification.trim()) ? '#9ca3af'
                          : decisionType?.startsWith('ACCEPTED') ? '#15803d' : '#dc2626',
                        color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                      {claimActionLoading ? '⏳' : 'Wyślij'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {selected?.type === 'CLAIM' && (
          <div style={{ padding: '8px 16px', background: '#fff', borderBottom: '1px solid #e5e7eb',
            display: 'flex', gap: 8 }}>
            <button
              disabled={selected.return_requested}
              onClick={() => setClaimAction('return')}
              style={{ flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 8,
                border: '1px solid #bfdbfe',
                background: selected.return_requested ? '#f3f4f6' : '#eff6ff',
                color: selected.return_requested ? '#9ca3af' : '#1d4ed8',
                cursor: selected.return_requested ? 'not-allowed' : 'pointer' }}>
              {selected.return_requested ? '✓ Zwrot zażądany' : '📦 Żądaj zwrotu'}
            </button>
            <button
              disabled={selected.decision_made}
              onClick={() => setClaimAction('decision')}
              style={{ flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 8,
                border: '1px solid #fde68a',
                background: selected.decision_made ? '#f3f4f6' : '#fefce8',
                color: selected.decision_made ? '#9ca3af' : '#92400e',
                cursor: selected.decision_made ? 'not-allowed' : 'pointer' }}>
              {selected.decision_made ? '✓ Decyzja wydana' : '⚖️ Decyzja'}
            </button>
          </div>
        )}
        {!hideHeader && (
          <div style={{ background: '#1d4ed8', color: '#fff', padding: '12px 16px' }}>
            <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 2 }}>{typeLabels[selected.type]} – {selected.account_name}</div>
            <div style={{ fontWeight: 600, fontSize: 14 }}><span title="Kliknij aby skopiować" onClick={() => { navigator.clipboard.writeText(selected.buyer?.login || ''); setCopyToast(selected.buyer?.login || ''); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>{selected.buyer?.login}</span></div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{translateSubject(selected.subject)}</div>
            <div style={{ marginTop: 8 }}>
              <button onClick={async () => {
                if (selected.is_handled) {
                  await axios.post(`${API}/allegro/issues/${selected.id}/unhandle`);
                  setSelected(prev => prev ? {...prev, is_handled: false} : null);
                  fetchIssues(0);
                } else {
                  await axios.post(`${API}/allegro/issues/${selected.id}/handle`);
                  setSelected(null);
                  setMobileView('list');
                  fetchIssues(0);
                }
              }} style={{ fontSize: 12, padding: '4px 14px', borderRadius: 10,
                border: selected?.is_handled ? '1px solid #fde68a' : '1px solid rgba(255,255,255,0.4)',
                background: selected?.is_handled ? '#fffbeb' : 'rgba(255,255,255,0.15)',
                color: selected?.is_handled ? '#92400e' : '#fff', cursor: 'pointer', fontWeight: 500 }}>
                {selected?.is_handled ? '↩ Nowe' : '✓ Zakończ'}
              </button>
              {(currentUser?.role === 'admin' || currentUser?.can_assign) && (
                <button onClick={async () => {
                  setShowIssueHistory(h => !h);
                  if (!showIssueHistory) {
                    setIssueHistoryLoading(true);
                    const res = await axios.get(`${API}/allegro/issues/${selected.id}/history`);
                    setIssueHistory(res.data || []);
                    setIssueHistoryLoading(false);
                  }
                }} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)',
                  color: '#fff', cursor: 'pointer', fontWeight: 500, marginLeft: 6 }}>
                  🕐
                </button>
              )}
                <button onClick={fetchBuyerOrders} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(147,197,253,0.3)',
                  color: '#fff', cursor: 'pointer', fontWeight: 500, marginLeft: 6 }}>
                  🛒
                </button>
                <BuyerReturnsButton buyerLogin={selected.buyer?.login || ''} variant='mobile' onOpenReturn={onOpenReturn} />
                <BuyerShipmentsButton buyerLogin={selected.buyer?.login || ''} variant='mobile' onOpenShipment={onOpenShipment} />
                  {issueLang !== 'pl' && (
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11,
                      background: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 500, marginLeft: 6 }}>
                      🌐 {issueLang.toUpperCase()}
                    </span>
                  )}
            </div>
          </div>
        )}

        {showIssueHistory && (currentUser?.role === 'admin' || currentUser?.can_assign) && (
          <div style={{ padding: '6px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11 }}>
            {issueHistoryLoading && <div style={{ color: '#9ca3af' }}>Ładowanie...</div>}
            {!issueHistoryLoading && issueHistory.length === 0 && <div style={{ color: '#9ca3af' }}>Brak historii</div>}
            {issueHistory.map((h, i) => (
              <div key={h.id} style={{ display: 'flex', gap: 6, paddingBottom: 3, marginBottom: 3, borderBottom: i < issueHistory.length-1 ? '1px solid #e5e7eb' : 'none' }}>
                <span>{h.event === 'handled' ? '✅' : h.event === 'assigned' ? '👤' : h.event === 'replied' ? '✉️' : '•'}</span>
                <div>
                  <span style={{ fontWeight: 500, color: '#374151' }}>
                    {h.event === 'handled' ? 'Zakończono' : h.event === 'assigned' ? `→ ${h.meta?.assigned_to || '—'}` : h.event === 'replied' ? 'Odpowiedź' : h.event}
                  </span>
                  <span style={{ color: '#9ca3af', marginLeft: 4 }}>{h.actor_name}</span>
                  <div style={{ color: '#9ca3af', fontSize: 10 }}>{h.created_at ? new Date(h.created_at).toLocaleString('pl-PL') : ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px', paddingTop: '16px', background: '#f9fafb' }}>
          {loadingChat && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>Ładowanie...</div>}
          {!loadingChat && (issueMeta?.subject || issueMeta?.description) && (
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }}>
              {issueMeta?.subject && (
                <div style={{ marginBottom: issueMeta?.description ? 6 : 0 }}>
                  <span style={{ color: '#6b7280', fontWeight: 600 }}>Powód: </span>
                  <span style={{ color: '#111827' }}>{translateSubject(issueMeta.subject)}</span>
                </div>
              )}
              {issueMeta?.description && (
                <div style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                  <span style={{ color: '#6b7280', fontWeight: 600 }}>Oczekiwane rozwiązanie: </span>
                  {issueMeta.description}
                </div>
              )}
            </div>
          )}
          {chat.map(msg => {
            const isBuyer = msg.author?.role === 'BUYER' || msg.author?.role === 'ALLEGRO_ADVISOR' || msg.author?.role === 'SYSTEM';
            const isAdmin = msg.author?.role === 'ADMIN';
            return (
              <div key={msg.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column',
                alignItems: isBuyer ? 'flex-start' : isAdmin ? 'center' : 'flex-end' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>
                  {isBuyer ? '👤' : isAdmin ? '🏛' : '🏪'} {msg.author?.login || msg.author?.role} · {formatDate(msg.createdAt)}
                </div>
                {(msg.text || !msg.attachments || msg.attachments.length === 0) && <div style={{ maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: 13, lineHeight: 1.5,
                  background: isBuyer ? '#fff' : isAdmin ? '#fefce8' : '#1d4ed8',
                  color: isBuyer ? '#374151' : isAdmin ? '#92400e' : '#fff',
                  border: isBuyer ? '1px solid #e5e7eb' : isAdmin ? '1px solid #fcd34d' : 'none' }}>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: msg.text || '' }} />
                  {isBuyer && issueLang !== 'pl' && msg.text && (
                    <div style={{ marginTop: 6, borderTop: '1px solid #f3f4f6', paddingTop: 6 }}>
                      {showTranslation[msg.id] ? (
                        <div>
                          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>🇵🇱 Tłumaczenie:</div>
                          <div style={{ fontSize: 12, color: '#374151' }}>{translations[msg.id] || '⏳ Tłumaczę...'}</div>
                          <button onClick={() => setShowTranslation(prev => ({...prev, [msg.id]: false}))}
                            style={{ fontSize: 11, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}>Ukryj</button>
                        </div>
                      ) : (
                        <button onClick={async () => {
                          setShowTranslation(prev => ({...prev, [msg.id]: true}));
                          if (!translations[msg.id]) {
                            const res = await axios.post(`${API}/ai/translate-to-polish`, { text: msg.text, source_lang: issueLang });
                            setTranslations(prev => ({...prev, [msg.id]: res.data.translation}));
                          }
                        }} style={{ fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                          🇵🇱 Pokaż tłumaczenie
                        </button>
                      )}
                    </div>
                  )}
                  {!isBuyer && !isAdmin && issueLang !== 'pl' && msg.text && (
                    <div style={{ marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 6 }}>
                      {showTranslation['seller_' + msg.id] ? (
                        <div>
                          <div style={{ fontSize: 11, color: '#bfdbfe', marginBottom: 3 }}>🇵🇱 Wersja polska:</div>
                          <div style={{ fontSize: 12, color: '#fff' }}>{translations['seller_' + msg.id] || '⏳ Tłumaczę...'}</div>
                          <button onClick={() => setShowTranslation(prev => ({...prev, ['seller_' + msg.id]: false}))}
                            style={{ fontSize: 11, color: '#bfdbfe', background: 'none', border: 'none', cursor: 'pointer' }}>Ukryj</button>
                        </div>
                      ) : (
                        <button onClick={async () => {
                          setShowTranslation(prev => ({...prev, ['seller_' + msg.id]: true}));
                          if (!translations['seller_' + msg.id]) {
                            const res = await axios.post(`${API}/ai/translate-to-polish`, { text: msg.text, source_lang: issueLang });
                            setTranslations(prev => ({...prev, ['seller_' + msg.id]: res.data.translation}));
                          }
                        }} style={{ fontSize: 11, color: '#bfdbfe', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                          🇵🇱 Pokaż po polsku
                        </button>
                      )}
                    </div>
                  )}
                </div>}
                {msg.attachments && msg.attachments.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {msg.attachments.map((att: any, i: number) => (
                      <div key={i} style={{ marginBottom: 4 }}>
                        {att.fileName && att.fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <a href={att.proxy_url ? `/api${att.proxy_url}&token=${getAuthToken()}` : att.url} target="_blank" rel="noreferrer">
                            <img src={att.proxy_url ? `/api${att.proxy_url}&token=${getAuthToken()}` : att.url}
                              alt={att.fileName}
                              style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8,
                                border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', display: 'block' }} />
                          </a>
                        ) : (
                          <a href={att.proxy_url ? `/api${att.proxy_url}&token=${getAuthToken()}` : att.url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 12, color: '#1d4ed8' }}>
                            📎 {att.fileName || 'Załącznik'}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              </div>
            );
          })}
          {lockInfo && (
            <div style={{ margin: '10px 0', padding: '10px 14px', background: '#fef9c3',
              border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
              ⚠️ {lockInfo} – możesz przeglądać ale uważaj na konflikty
            </div>
          )}
          {!loadingChat && chat.length >= 100 && !!issueMeta?.messagesCount && issueMeta.messagesCount > chat.length && (
            <div style={{ marginTop: 12, marginBottom: 4, padding: '10px 12px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠️ Widzisz tylko {chat.length} z {issueMeta.messagesCount} wiadomości</div>
              <div>Allegro API udostępnia tylko 100 najstarszych wiadomości w sprawie (znany błąd Allegro, bez terminu poprawki). Najnowsze wiadomości, w tym odpowiedzi doradcy Allegro i Twoje, są widoczne tylko w Sales Center.</div>
              {selected?.type === 'CLAIM' && (
                <a href={`https://salescenter.allegro.com/claims/${selected.id}`} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-block', marginTop: 6, color: '#92400e', fontWeight: 600 }}>Otwórz tę reklamację w Sales Center ↗</a>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        {true && (
          <div style={{ padding: '10px 12px', background: '#fff', borderTop: '1px solid #e5e7eb' }}>
            {sent && <div style={{ fontSize: 12, color: sent.includes('Błąd') ? '#dc2626' : '#15803d', marginBottom: 6 }}>{sent}</div>}
            <div style={{ fontSize: 11, color: '#7B79C9', marginBottom: 6, padding: '4px 8px', background: '#EEEDFE', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>🌐 Klient otrzyma odpowiedź w języku:</span>
              <select value={issueLang} onChange={e => { setIssueLang(e.target.value); setReplyTranslation(''); }} style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #C4C2F5', borderRadius: 6, color: '#3C3489', background: '#fff', cursor: 'pointer' }}>
                {[{v:'pl',l:'🇵🇱 Polski'},{v:'en',l:'🇬🇧 Angielski'},{v:'de',l:'🇩🇪 Niemiecki'},{v:'cs',l:'🇨🇿 Czeski'},{v:'sk',l:'🇸🇰 Słowacki'},{v:'hu',l:'🇭🇺 Węgierski'},{v:'ro',l:'🇷🇴 Rumuński'},{v:'uk',l:'🇺🇦 Ukraiński'},{v:'ru',l:'🇷🇺 Rosyjski'},{v:'fr',l:'🇫🇷 Francuski'},{v:'it',l:'🇮🇹 Włoski'}].map((o: any) => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <DictateButton disabled={sending} onText={(t: string) => { const cur = reply || ''; handleReplyChange(cur ? cur + ' ' + t : t, setReply); }} />
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <textarea value={reply} onChange={e => handleReplyChange(e.target.value, setReply)} onInput={(e:any)=>{const t=e.currentTarget;t.style.height='auto';t.style.height=Math.min(t.scrollHeight,200)+'px';}}
              onKeyDown={e => { if (e.key === 'Escape') setShowReplySuggestions(false); }}
              placeholder="Wpisz wiadomość..." rows={3}
              ref={replyTextareaRef}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 8,
                fontSize: 13, fontFamily: 'system-ui', resize: 'none', outline: 'none', boxSizing: 'border-box' }} />
            {showReplySuggestions && <ShortcutSuggestions suggestions={replySuggestions} onSelect={s => applyReplyShortcut(s, reply, setReply)} onClose={() => setShowReplySuggestions(false)} />}
            </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                <label style={{ padding: '5px 10px', background: '#f3f4f6', border: '1px solid #d1d5db',
                  borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#374151' }}>
                  📎 {uploadingFile ? '...' : 'Załącz'}
                  <input type="file" style={{ display: 'none' }}
                    accept="image/png,image/jpeg,image/gif,image/bmp,image/tiff,application/pdf"
                    onChange={e => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }}
                    disabled={uploadingFile} />
                </label>
                {attachments.map((a, idx) => (
                  <span key={idx} style={{ padding: '3px 8px', background: '#dbeafe', borderRadius: 6,
                    fontSize: 11, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                    📄 {a.filename}
                    <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14, padding: 0 }}>✕</button>
                  </span>
                ))}
              </div>
              {showOfferPicker && (
                <OfferPicker
                    defaultAccountId={selected?.account_id}
                    onClose={() => setShowOfferPicker(false)}
                    onInsertLink={(link) => {
                      const ta = replyTextareaRef.current;
                      if (ta) {
                        const start = ta.selectionStart;
                        const end = ta.selectionEnd;
                        const newVal = reply.slice(0, start) + link + reply.slice(end);
                        setReply(newVal);
                        setTimeout(() => { ta.focus(); ta.setSelectionRange(start + link.length, start + link.length); }, 0);
                      } else {
                        setReply(prev => prev + link);
                      }
                      setShowOfferPicker(false);
                    }}
                />
              )}
              <button onClick={() => setShowOfferPicker(v => !v)}
                style={{ padding: '10px 12px', background: showOfferPicker ? '#1d4ed8' : '#f0f9ff',
                  color: showOfferPicker ? '#fff' : '#0369a1', border: '1px solid #bae6fd',
                  borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                🏷️
              </button>
              <button onClick={() => sendMessage(false)} disabled={!reply.trim() || sending}
                style={{ flex: 1, padding: '10px', background: (reply.trim() && !sending) ? '#1d4ed8' : '#9ca3af',
                  color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                {sending ? 'Wysyłanie...' : 'Wyślij'}
              </button>
              <button onClick={() => sendMessage(true)} disabled={!reply.trim() || sending}
                style={{ flex: 1, padding: '10px', background: (reply.trim() && !sending) ? '#f0fdf4' : '#f9fafb',
                  color: (reply.trim() && !sending) ? '#15803d' : '#9ca3af', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                {sending ? 'Wysyłanie...' : 'Wyślij i ucz'}
              </button>
              <button onClick={generateAI} disabled={aiLoading}
                style={{ padding: '10px 12px', background: '#f0fdf4', color: '#15803d',
                  border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                {aiLoading ? '⏳' : '✦ AI'}
              </button>
            </div>
          </div>
        )}
        {!hideHeader && (
          <div style={{ display: 'flex', background: '#1d4ed8', padding: '8px 0', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <button onClick={() => setMobileView('list')}
              style={{ flex: 1, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11, borderRadius: 8, margin: '0 8px' }}>
              <div style={{ fontSize: 20 }}>←</div><div>Wróć</div>
            </button>
          </div>
        )}
      {copyToast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1d4ed8', color: '#fff', padding: '8px 18px', borderRadius: 20,
          fontSize: 13, fontWeight: 500, zIndex: 9999, pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          {"✓ Skopiowano: "}{copyToast}
        </div>
      )}
      </div>
    );
  }

  return (
    <>
      {((accountsLoaded && accounts.length === 0) || justReturnedFromAllegro) && !allegroBannerClosed && (
        <MMAllegroSetupBanner
          message="Aby odpowiadać i zarządzać reklamacjami i dyskusjami od kupujących, podłącz konto Allegro."
          returnModule="issues"
          showCongrats={justReturnedFromAllegro}
          onClose={() => setAllegroBannerClosed(true)}
        />
      )}
      {copyToast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1d4ed8', color: '#fff', padding: '8px 18px', borderRadius: 20,
          fontSize: 13, fontWeight: 500, zIndex: 9999, pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          ✓ Skopiowano: {copyToast}
        </div>
      )}
      {showOrders && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowOrders(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 560, maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>🛒 Zamówienia: {selected?.buyer?.login}</div>
              <button onClick={() => setShowOrders(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            {loadingOrders && <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Ładowanie...</div>}
            {!loadingOrders && buyerOrders.length === 0 && (
              <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Brak zamówień</div>
            )}
            {!loadingOrders && buyerOrders.map((o, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                {o.image && <img src={o.image} alt={o.offer_name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
                {!o.image && <div style={{ width: 56, height: 56, background: '#f3f4f6', borderRadius: 6, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.offer_name}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {o.sku && <span>SKU: {o.sku}</span>}
                    {o.offer_id && <span>ID: {o.offer_id}</span>}
                    <span>Ilość: {o.quantity}</span>
                    {o.bought_at && <span>{new Date(o.bought_at).toLocaleDateString('pl-PL')}</span>}
{(o as any).smart ? <span style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 4, padding: '0 6px', fontWeight: 600 }}>Smart</span> : <span style={{ color: '#9ca3af' }}>bez Smart</span>}
{(o as any).apilo_id && <span title="Kliknij, aby skopiować" onClick={() => { try { navigator.clipboard.writeText(String((o as any).apilo_id)); } catch (e) {} }} style={{ cursor: 'pointer' }}>Apilo: <b style={{ color: '#6d28d9' }}>{(o as any).apilo_id}</b></span>}
                  </div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                  {o.price} {o.currency}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: hideHeader ? '100%' : '100dvh', fontFamily: 'system-ui, sans-serif', fontSize: 14 }}>
      {!hideHeader && <LiveAlertBar currentUser={currentUser} onOpenAiOpen={onOpenAiOpen} onOpenReturns={onOpenReturns} />}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {/* SIDEBAR */}
      <div style={{ width: 320, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', background: '#f9fafb' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>⚖️ Dyskusje i reklamacje</span>
            <button onClick={onBack} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6,
              border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>
              ← Wróć
            </button>
          </div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') fetchIssues(0); }}
              placeholder="Szukaj po loginie..."
              style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6,
                fontSize: 12, boxSizing: 'border-box', outline: 'none' }} />
            <button onClick={() => { setIssuePage(0); fetchIssues(0); }}
              style={{ padding: '6px 10px', background: '#1d4ed8', color: '#fff', border: 'none',
                borderRadius: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Szukaj
            </button>
            {search && <button onClick={() => { setSearch(''); fetchIssues(0); }}
              style={{ padding: '6px 8px', background: '#f3f4f6', color: '#6b7280', border: 'none',
                borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>✕</button>}
          </div>
          <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}
            style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6,
              fontSize: 12, background: '#fff', marginBottom: 6 }}>
            <option value="all">Wszystkie konta</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" title="Zaznacz wszystkie na stronie"
              checked={filtered.slice(issuePage * ISSUE_PAGE_SIZE, (issuePage + 1) * ISSUE_PAGE_SIZE).length > 0 && filtered.slice(issuePage * ISSUE_PAGE_SIZE, (issuePage + 1) * ISSUE_PAGE_SIZE).every(i => selectedIssueIds.has(i.id))}
              onChange={e => {
                const pageIds = filtered.slice(issuePage * ISSUE_PAGE_SIZE, (issuePage + 1) * ISSUE_PAGE_SIZE).map(i => i.id);
                if (e.target.checked) setSelectedIssueIds(new Set([...Array.from(selectedIssueIds), ...pageIds]));
                else { const next = new Set(Array.from(selectedIssueIds)); pageIds.forEach(id => next.delete(id)); setSelectedIssueIds(next); }
              }}
              style={{ cursor: 'pointer', flexShrink: 0 }} />
            {[{k:'open',l:'Nowe'},{k:'all',l:'Wszystkie'}].map(f => (
              <button key={f.k} onClick={() => { setFilter(f.k); fetchIssues(0, f.k); }}
                style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, border: '1px solid #d1d5db',
                  background: filter === f.k ? '#1d4ed8' : 'transparent',
                  color: filter === f.k ? '#fff' : '#6b7280', cursor: 'pointer', flex: 1 }}>
                {f.l}
              </button>
            ))}
            <button onClick={() => fetchIssues(0)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20,
              border: '1px solid #d1d5db', background: 'transparent', color: '#6b7280', cursor: 'pointer' }}>↻</button>
            <AssignButton
              selectedIds={Array.from(selectedIssueIds)}
              endpoint="/api/assign/issues"
              currentUser={currentUser}
              onAssigned={() => { setSelectedIssueIds(new Set()); fetchIssues(0); }}
              size="small"
            />
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 16, color: '#6b7280', fontSize: 12 }}>Ładowanie...</div>}
          {filtered.slice(issuePage * ISSUE_PAGE_SIZE, (issuePage + 1) * ISSUE_PAGE_SIZE).map(issue => {
            const statusStyle = statusColors[issue.currentState?.status] || { bg: '#f3f4f6', color: '#6b7280', label: issue.currentState?.status };
            return (
              <div key={issue.id}
                style={{ padding: '10px 14px', borderBottom: '1px solid #e5e7eb', cursor: 'pointer',
                  background: selectedIssueIds.has(issue.id) ? '#fef9c3' : selected?.id === issue.id ? '#eff6ff' : '#fff',
                  borderLeft: selected?.id === issue.id ? '3px solid #1d4ed8' : '3px solid transparent',
                  display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={selectedIssueIds.has(issue.id)}
                  onChange={e => { e.stopPropagation(); const next = new Set(selectedIssueIds); if (e.target.checked) next.add(issue.id); else next.delete(issue.id); setSelectedIssueIds(next); }}
                  onClick={e => e.stopPropagation()}
                  style={{ marginTop: 3, flexShrink: 0, cursor: 'pointer' }} />
                <div style={{ flex: 1, minWidth: 0 }} onClick={() => selectIssue(issue)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontWeight: 500, fontSize: 12 }}>
                    {typeLabels[issue.type] || issue.type} – {issue.language && issue.language !== 'pl' && <img src={`/flags/svg/${issue.language}.svg`} alt={issue.language} width='17' height='13' style={{verticalAlign:'middle',marginRight:2}} />}<span title="Kliknij aby skopiować" onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(issue.buyer?.login || ''); setCopyToast(issue.buyer?.login || ''); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>{issue.buyer?.login}</span>
                  </span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDate(issue.openedDate)}</span>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, 
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {translateSubject(issue.subject)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8,
                    background: statusStyle.bg, color: statusStyle.color }}>
                    {statusStyle.label}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {issue.assigned_to && (() => { const agent = (window as any).__agents__?.find((a: any) => a.id === issue.assigned_to); return agent ? <div title={agent.name} style={{ width: 8, height: 8, borderRadius: '50%', background: agent.color || '#9ca3af', flexShrink: 0 }} /> : null; })()}
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>{issue.account_name}</span>
                  </div>
                </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && !loading && (
            <div style={{ padding: 20, color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>Brak dyskusji</div>
          )}
        </div>
        <div style={{ padding: '8px 12px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => { setIssuePage(p => Math.max(0, p-1)); }}
            disabled={issuePage === 0}
            style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db',
              background: 'transparent', cursor: issuePage === 0 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>←</button>
          <span style={{ fontSize: 11, color: '#6b7280', flex: 1, textAlign: 'center' }}>
            {issuePage+1}/{Math.max(1, Math.ceil(filtered.length/ISSUE_PAGE_SIZE))} · {filtered.length} spraw
          </span>
          <button onClick={() => { setIssuePage(p => p+1); }}
            disabled={(issuePage+1) * ISSUE_PAGE_SIZE >= filtered.length}
            style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db',
              background: 'transparent', cursor: (issuePage+1)*ISSUE_PAGE_SIZE >= totalIssues ? 'not-allowed' : 'pointer', color: '#6b7280' }}>→</button>
        </div>
      </div>

      {/* MODAL AKCJI REKLAMACYJNYCH */}
      {claimAction && selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 460, maxWidth: '95vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto' }}>

            {claimAction === 'return' && (
              <>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>📦 Odesłanie reklamowanego towaru</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
                  Wybierz opcję odesłania towaru przez kupującego.
                </div>

                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Sposób odesłania</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button onClick={() => setReturnType('RETURN_REQUIRED_SELLER_LABEL')}
                      style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', textAlign: 'left',
                        border: '2px solid ' + (returnType === 'RETURN_REQUIRED_SELLER_LABEL' ? '#1d4ed8' : '#d1d5db'),
                        background: returnType === 'RETURN_REQUIRED_SELLER_LABEL' ? '#eff6ff' : 'transparent',
                        color: returnType === 'RETURN_REQUIRED_SELLER_LABEL' ? '#1d4ed8' : '#374151',
                        fontWeight: returnType === 'RETURN_REQUIRED_SELLER_LABEL' ? 600 : 400 }}>
                      <div>📦 Zaplanuj odesłanie – etykieta od sprzedającego</div>
                      <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Sprzedający pokrywa koszt zwrotu</div>
                    </button>
                    <button onClick={() => setReturnType('RETURN_REQUIRED_CUSTOM')}
                      style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', textAlign: 'left',
                        border: '2px solid ' + (returnType === 'RETURN_REQUIRED_CUSTOM' ? '#1d4ed8' : '#d1d5db'),
                        background: returnType === 'RETURN_REQUIRED_CUSTOM' ? '#eff6ff' : 'transparent',
                        color: returnType === 'RETURN_REQUIRED_CUSTOM' ? '#1d4ed8' : '#374151',
                        fontWeight: returnType === 'RETURN_REQUIRED_CUSTOM' ? 600 : 400 }}>
                      <div>🚚 Zaplanuj odesłanie – własna wysyłka kupującego</div>
                      <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Kupujący organizuje i pokrywa koszt zwrotu</div>
                    </button>
                    <button onClick={() => setReturnType('RETURN_NOT_REQUIRED')}
                      style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', textAlign: 'left',
                        border: '2px solid ' + (returnType === 'RETURN_NOT_REQUIRED' ? '#15803d' : '#d1d5db'),
                        background: returnType === 'RETURN_NOT_REQUIRED' ? '#f0fdf4' : 'transparent',
                        color: returnType === 'RETURN_NOT_REQUIRED' ? '#15803d' : '#374151',
                        fontWeight: returnType === 'RETURN_NOT_REQUIRED' ? 600 : 400 }}>
                      <div>✓ Bez odesłania</div>
                      <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Nie wymagamy zwrotu towaru</div>
                    </button>
                    {selected?.type === 'CLAIM' && (
                      <a href={`https://salescenter.allegro.com/claims/${selected.id}`} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'block', padding: '10px 14px', borderRadius: 8, fontSize: 13, textAlign: 'left', textDecoration: 'none',
                          border: '2px dashed #fb923c', background: '#fff7ed', color: '#9a3412' }}>
                        <div style={{ fontWeight: 600 }}>🎁 Bezpłatny zwrot w ramach Allegro Smart! ↗</div>
                        <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>Otwiera tę reklamację w Allegro Sales Center (zaloguj się na konto {selected.account_name}) – tam wybierz „Zaplanuj odesłanie" → „Bezpłatny zwrot w ramach Allegro Smart!". Opcja dostępna tylko dla zamówień Smart!, dopóki nie minął termin na odstąpienie od umowy.</div>
                      </a>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Wiadomość do kupującego (opcjonalnie)</div>
                  {replyTemplates.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Wzory odpowiedzi:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {replyTemplates.map(t => (
                          <button key={t.id} onClick={() => setReturnText(t.text)}
                            style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20,
                              border: '1px solid #bfdbfe', background: '#eff6ff',
                              color: '#1d4ed8', cursor: 'pointer' }}>
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <textarea value={returnText} onChange={e => setReturnText(e.target.value)}
                    placeholder={returnType === 'RETURN_NOT_REQUIRED'
                      ? 'np. Nie wymagamy odesłania towaru. Możesz go zachować.'
                      : 'np. Prosimy o odesłanie towaru na adres: ul. Przykładowa 1, 00-001 Warszawa'}
                    rows={3}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 6,
                      fontSize: 13, fontFamily: 'system-ui', resize: 'vertical', outline: 'none',
                      boxSizing: 'border-box' }} />
                  {returnType === 'RETURN_REQUIRED_SELLER_LABEL' && (
                    <div style={{ marginBottom: 12, marginTop: 8 }}>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                        📎 Etykieta zwrotna (wymagana gdy sprzedający pokrywa koszt):
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <label style={{ padding: '5px 12px', background: '#f3f4f6', border: '1px solid #d1d5db',
                          borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#374151' }}>
                          📎 {uploadingFile ? 'Uploading...' : 'Załącz etykietę'}
                          <input type="file" style={{ display: 'none' }}
                            accept="image/png,image/jpeg,image/gif,image/bmp,image/tiff,application/pdf"
                            onChange={async e => {
                              if (e.target.files?.[0] && selected) {
                                setUploadingFile(true);
                                try {
                                  const formData = new FormData();
                                  formData.append('account_id', selected.account_id || '');
                                  formData.append('file', e.target.files[0]);
                                  const res = await axios.post(`${API}/allegro/upload-attachment`, formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  setReturnAttachments([{ id: res.data.attachment_id, filename: res.data.filename }]);
                                } catch (err: any) {
                                  alert('Błąd uploadu: ' + (err.response?.data?.detail || 'nieznany'));
                                }
                                setUploadingFile(false);
                              }
                              e.target.value = '';
                            }}
                            disabled={uploadingFile} />
                        </label>
                        {returnAttachments.map((a, idx) => (
                          <span key={idx} style={{ padding: '3px 8px', background: '#dbeafe', borderRadius: 6,
                            fontSize: 12, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                            📄 {a.filename}
                            <button onClick={() => setReturnAttachments([])}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14, padding: 0 }}>✕</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {claimActionMsg && (
                  <div style={{ fontSize: 12, marginBottom: 12, padding: '8px 12px', borderRadius: 6,
                    background: claimActionMsg.includes('Błąd') ? '#fef2f2' : '#f0fdf4',
                    color: claimActionMsg.includes('Błąd') ? '#dc2626' : '#15803d' }}>
                    {claimActionMsg}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setClaimAction(null); setClaimActionMsg(''); setReturnText(''); }}
                    style={{ padding: '8px 18px', border: '1px solid #d1d5db', background: 'transparent',
                      borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#6b7280' }}>
                    Anuluj
                  </button>
                  <button onClick={async () => {
                    setClaimActionLoading(true);
                    try {
                      const res = await axios.post(`${API}/allegro/issues/${selected.id}/request-return`, {
                        account_id: selected.account_id,
                        return_type: returnType,
                        text: returnText
                      });
                      if (res.data?.data?.status_code >= 400) {
                        setClaimActionMsg('Błąd: ' + JSON.stringify(res.data?.data));
                      } else {
                        setClaimActionMsg('✓ Wysłano informację o odesłaniu!');
                        setSelected({...selected, return_requested: true});
                        setTimeout(() => { setClaimAction(null); setClaimActionMsg(''); setReturnText(''); fetchIssues(0); }, 1500);
                      }
                    } catch(e: any) {
                      setClaimActionMsg('Błąd: ' + (e.response?.data?.detail || JSON.stringify(e.response?.data) || 'nieznany'));
                    }
                    setClaimActionLoading(false);
                  }} disabled={claimActionLoading}
                    style={{ padding: '8px 18px', background: '#1d4ed8', color: '#fff',
                      border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                    {claimActionLoading ? '⏳ Wysyłam...' : 'Wyślij'}
                  </button>
                </div>
              </>
            )}

            {claimAction === 'decision' && (
              <>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>⚖️ Decyzja reklamacyjna</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
                  Wybierz decyzję i opcjonalnie wpisz uzasadnienie dla kupującego.
                </div>

                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Uznanie reklamacji</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                    {[
                      { value: 'ACCEPTED_REPAIR', label: '🔧 Naprawa towaru' },
                      { value: 'ACCEPTED_EXCHANGE', label: '🔄 Wymiana towaru' },
                      { value: 'ACCEPTED_REFUND', label: '💰 Zwrot pełnej kwoty' },
                      { value: 'ACCEPTED_PARTIAL_REFUND', label: '💸 Zwrot częściowy' },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setDecisionType(opt.value as any)}
                        style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', textAlign: 'left',
                          border: '2px solid ' + (decisionType === opt.value ? '#15803d' : '#d1d5db'),
                          background: decisionType === opt.value ? '#f0fdf4' : 'transparent',
                          color: decisionType === opt.value ? '#15803d' : '#374151',
                          fontWeight: decisionType === opt.value ? 600 : 400 }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Odrzucenie reklamacji</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      { value: 'REJECTED_OTHER', label: '✗ Odrzucenie – inny powód' },
                      { value: 'REJECTED_PRODUCT_NOT_RETURNED', label: '✗ Produkt nie został odesłany' },
                      { value: 'REJECTED_PRODUCT_DAMAGED_BY_USER', label: '✗ Produkt uszkodzony przez użytkownika' },
                      { value: 'REJECTED_PRODUCT_CONFORMS_TO_CONTRACT', label: '✗ Produkt zgodny z umową' },
                      { value: 'REJECTED_MINOR_DEFECT', label: '✗ Drobna wada – nie uzasadnia reklamacji' },
                      { value: 'REJECTED_ADDITIONAL_REQUIREMENTS_NOT_COMPLETED', label: '✗ Nie spełniono wymogów dodatkowych' },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setDecisionType(opt.value as any)}
                        style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', textAlign: 'left',
                          border: '2px solid ' + (decisionType === opt.value ? '#dc2626' : '#d1d5db'),
                          background: decisionType === opt.value ? '#fef2f2' : 'transparent',
                          color: decisionType === opt.value ? '#dc2626' : '#374151',
                          fontWeight: decisionType === opt.value ? 600 : 400 }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                    Wiadomość do kupującego {decisionType?.startsWith('REJECTED') ? '(wymagana)' : '(opcjonalna)'}
                  </div>
                  {decisionTemplates.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Wzory odpowiedzi:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {decisionTemplates.map(t => (
                          <button key={t.id} onClick={() => setDecisionJustification(t.text)}
                            style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20,
                              border: '1px solid #fde68a', background: '#fefce8',
                              color: '#92400e', cursor: 'pointer' }}>
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <textarea value={decisionJustification} onChange={e => setDecisionJustification(e.target.value)}
                    placeholder={decisionType?.startsWith('ACCEPTED')
                      ? 'np. Reklamacja uznana. Wyślemy nowy towar w ciągu 3 dni roboczych.'
                      : 'np. Reklamacja odrzucona. Produkt nosi ślady uszkodzeń mechanicznych.'}
                    rows={4}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 6,
                      fontSize: 13, fontFamily: 'system-ui', resize: 'vertical', outline: 'none',
                      boxSizing: 'border-box' }} />
                </div>

                {claimActionMsg && (
                  <div style={{ fontSize: 12, marginBottom: 12, padding: '8px 12px', borderRadius: 6,
                    background: claimActionMsg.includes('Błąd') ? '#fef2f2' : '#f0fdf4',
                    color: claimActionMsg.includes('Błąd') ? '#dc2626' : '#15803d' }}>
                    {claimActionMsg}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setClaimAction(null); setClaimActionMsg(''); setDecisionJustification(''); }}
                    style={{ padding: '8px 18px', border: '1px solid #d1d5db', background: 'transparent',
                      borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#6b7280' }}>
                    Anuluj
                  </button>
                  <button
                    disabled={claimActionLoading || (decisionType?.startsWith('REJECTED') && !decisionJustification.trim())}
                    onClick={async () => {
                      setClaimActionLoading(true);
                      try {
                        const res = await axios.post(`${API}/allegro/issues/${selected.id}/decision`, {
                          account_id: selected.account_id,
                          decision: decisionType,
                          message: decisionJustification
                        });
                        if (res.data?.data?.status_code >= 400) {
                          setClaimActionMsg('Błąd Allegro: ' + JSON.stringify(res.data?.data));
                        } else {
                          setClaimActionMsg('✓ Decyzja wysłana!');
                          setSelected({...selected, decision_made: true});
                          setTimeout(() => { setClaimAction(null); setClaimActionMsg(''); setDecisionJustification(''); fetchIssues(0); }, 1500);
                        }
                      } catch(e: any) {
                        setClaimActionMsg('Błąd: ' + (e.response?.data?.detail || JSON.stringify(e.response?.data) || 'nieznany'));
                      }
                      setClaimActionLoading(false);
                    }}
                    style={{ padding: '8px 18px',
                      background: claimActionLoading || (decisionType?.startsWith('REJECTED') && !decisionJustification.trim())
                        ? '#9ca3af'
                        : decisionType?.startsWith('ACCEPTED') ? '#15803d' : '#dc2626',
                      color: '#fff', border: 'none', borderRadius: 8, fontSize: 13,
                      cursor: claimActionLoading || (decisionType?.startsWith('REJECTED') && !decisionJustification.trim()) ? 'not-allowed' : 'pointer',
                      fontWeight: 600 }}>
                    {claimActionLoading ? '⏳ Wysyłam...' : decisionType?.startsWith('ACCEPTED') ? '✓ Wyślij decyzję' : '✗ Wyślij decyzję'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

            {/* DETAIL */}
      <div style={{ flex: 1, display: isMobile && mobileView === 'list' ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚖️</div>
              <div style={{ fontSize: 16, fontWeight: 500 }}>Wybierz sprawę</div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                {typeLabels[selected.type] || selected.type} – <span title="Kliknij aby skopiować" onClick={() => { navigator.clipboard.writeText(selected.buyer?.login || ''); setCopyToast(selected.buyer?.login || ''); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>{selected.buyer?.login}</span>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{translateSubject(selected.subject)}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10,
                  background: statusColors[selected.currentState?.status]?.bg || '#f3f4f6',
                  color: statusColors[selected.currentState?.status]?.color || '#6b7280' }}>
                  {statusColors[selected.currentState?.status]?.label || selected.currentState?.status}
                </span>
                <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: '#f0fdf4', color: '#15803d' }}>
                  {selected.account_name}
                </span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  Zamówienie: {selected.checkoutForm?.id?.substring(0,8)}...
                </span>
                {selected.currentState?.chatActive === false && (
                  <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: '#fef2f2', color: '#dc2626' }}>
                    Chat zamknięty
                  </span>
                )}
                <button onClick={async () => {
                  if (selected.is_handled) {
                    await axios.post(`${API}/allegro/issues/${selected.id}/unhandle`);
                    setSelected(prev => prev ? {...prev, is_handled: false} : null);
                    fetchIssues(0);
                  } else {
                    await axios.post(`${API}/allegro/issues/${selected.id}/handle`);
                    setSelected(null);
                    fetchIssues(0);
                  }
                }} style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                  border: selected?.is_handled ? '1px solid #fde68a' : '1px solid #bbf7d0',
                  background: selected?.is_handled ? '#fffbeb' : '#f0fdf4',
                  color: selected?.is_handled ? '#92400e' : '#15803d', cursor: 'pointer' }}>
                  {selected?.is_handled ? '↩ Nowe' : '✓ Zakończ'}
                </button>
                {(currentUser?.role === 'admin' || currentUser?.can_assign) && (
                  <button onClick={async () => {
                    setShowIssueHistory(h => !h);
                    if (!showIssueHistory) {
                      setIssueHistoryLoading(true);
                      const res = await axios.get(`${API}/allegro/issues/${selected.id}/history`);
                      setIssueHistory(res.data || []);
                      setIssueHistoryLoading(false);
                    }
                  }} style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                    border: '1px solid #e5e7eb', background: showIssueHistory ? '#f3f4f6' : '#fff',
                    color: '#6b7280', cursor: 'pointer' }}>
                    🕐 Historia
                  </button>
                )}
                  <button onClick={fetchBuyerOrders} style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                    border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}>
                    🛒 Zamówienia
                  </button>
                  <BuyerReturnsButton buyerLogin={selected.buyer?.login || ''} onOpenReturn={onOpenReturn} />
                  <BuyerShipmentsButton buyerLogin={selected.buyer?.login || ''} onOpenShipment={onOpenShipment} />
                  {issueLang !== 'pl' && (
                    <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11,
                      background: '#EEEDFE', color: '#3C3489', fontWeight: 500 }}>
                      🌐 {issueLang.toUpperCase()}
                    </span>
                  )}
                {selected?.type === 'CLAIM' && (
                  <>
                    <button
                      disabled={selected.return_requested}
                      onClick={() => setClaimAction('return')}
                      style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                        border: '1px solid #bfdbfe',
                        background: selected.return_requested ? '#f3f4f6' : '#eff6ff',
                        color: selected.return_requested ? '#9ca3af' : '#1d4ed8',
                        cursor: selected.return_requested ? 'not-allowed' : 'pointer' }}>
                      {selected.return_requested ? '✓ Zwrot zażądany' : '📦 Żądaj zwrotu'}
                    </button>
                    <button
                      disabled={selected.decision_made}
                      onClick={() => setClaimAction('decision')}
                      style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                        border: '1px solid #fde68a',
                        background: selected.decision_made ? '#f3f4f6' : '#fefce8',
                        color: selected.decision_made ? '#9ca3af' : '#92400e',
                        cursor: selected.decision_made ? 'not-allowed' : 'pointer' }}>
                      {selected.decision_made ? '✓ Decyzja wydana' : '⚖️ Decyzja reklamacyjna'}
                    </button>
                  </>
                )}
              </div>
            </div>
            {showIssueHistory && (currentUser?.role === 'admin' || currentUser?.can_assign) && (
              <div style={{ padding: '8px 18px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 12 }}>
                {issueHistoryLoading && <div style={{ color: '#9ca3af' }}>Ładowanie...</div>}
                {!issueHistoryLoading && issueHistory.length === 0 && <div style={{ color: '#9ca3af' }}>Brak historii</div>}
                {issueHistory.map((h, i) => (
                  <div key={h.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', paddingBottom: 4, marginBottom: 4, borderBottom: i < issueHistory.length-1 ? '1px solid #e5e7eb' : 'none' }}>
                    <span>{h.event === 'handled' ? '✅' : h.event === 'assigned' ? '👤' : h.event === 'replied' ? '✉️' : '•'}</span>
                    <div>
                      <span style={{ fontWeight: 500, color: '#374151' }}>
                        {h.event === 'handled' ? 'Zakończono' : h.event === 'assigned' ? `Przydzielono do: ${h.meta?.assigned_to || '—'}` : h.event === 'replied' ? 'Wysłano odpowiedź' : h.event}
                      </span>
                      <span style={{ color: '#9ca3af', marginLeft: 6 }}>{h.actor_name}</span>
                      <div style={{ color: '#9ca3af', fontSize: 11 }}>{h.created_at ? new Date(h.created_at).toLocaleString('pl-PL') : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ flex: 1, padding: '14px 18px', overflowY: 'auto', background: '#f9fafb' }}>
              {loadingChat && <div style={{ color: '#6b7280', fontSize: 12, textAlign: 'center', padding: 20 }}>Ładowanie czatu...</div>}
              {!loadingChat && (issueMeta?.subject || issueMeta?.description) && (
                <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }}>
                  {issueMeta?.subject && (
                    <div style={{ marginBottom: issueMeta?.description ? 6 : 0 }}>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>Powód: </span>
                      <span style={{ color: '#111827' }}>{translateSubject(issueMeta.subject)}</span>
                    </div>
                  )}
                  {issueMeta?.description && (
                    <div style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>Oczekiwane rozwiązanie: </span>
                      {issueMeta.description}
                    </div>
                  )}
                </div>
              )}
              {chat.map(msg => {
                const isBuyer = msg.author?.role === 'BUYER' || msg.author?.role === 'ALLEGRO_ADVISOR' || msg.author?.role === 'SYSTEM';
                const isAdmin = msg.author?.role === 'ADMIN';
                return (
                  <div key={msg.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column',
                    alignItems: isBuyer ? 'flex-start' : isAdmin ? 'center' : 'flex-end' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>
                      {isBuyer ? '👤' : isAdmin ? '🏛' : '🏪'} {msg.author?.login || msg.author?.role} · {formatDate(msg.createdAt)}
                    </div>
                    <div style={{ maxWidth: isAdmin ? '90%' : '72%', padding: '9px 13px', borderRadius: 12,
                      fontSize: 13, lineHeight: 1.6,
                      background: isBuyer ? '#fff' : isAdmin ? '#fefce8' : '#1d4ed8',
                      color: isBuyer ? '#374151' : isAdmin ? '#92400e' : '#fff',
                      border: isBuyer ? '1px solid #e5e7eb' : isAdmin ? '1px solid #fcd34d' : 'none' }}>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: msg.text || '' }} />
                      {isBuyer && issueLang !== 'pl' && msg.text && (
                        <div style={{ marginTop: 6, borderTop: '1px solid #f3f4f6', paddingTop: 6 }}>
                          {showTranslation[msg.id] ? (
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>🇵🇱 Tłumaczenie:</div>
                              <div style={{ fontSize: 12, color: '#374151' }}>{translations[msg.id] || '⏳ Tłumaczę...'}</div>
                              <button onClick={() => setShowTranslation(prev => ({...prev, [msg.id]: false}))}
                                style={{ fontSize: 11, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}>Ukryj</button>
                            </div>
                          ) : (
                            <button onClick={async () => {
                              setShowTranslation(prev => ({...prev, [msg.id]: true}));
                              if (!translations[msg.id]) {
                                const res = await axios.post(`${API}/ai/translate-to-polish`, { text: msg.text, source_lang: issueLang });
                                setTranslations(prev => ({...prev, [msg.id]: res.data.translation}));
                              }
                            }} style={{ fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                              🇵🇱 Pokaż tłumaczenie
                            </button>
                          )}
                        </div>
                      )}
                      {!isBuyer && !isAdmin && issueLang !== 'pl' && msg.text && (
                        <div style={{ marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 6 }}>
                          {showTranslation['seller_' + msg.id] ? (
                            <div>
                              <div style={{ fontSize: 11, color: '#bfdbfe', marginBottom: 3 }}>🇵🇱 Wersja polska:</div>
                              <div style={{ fontSize: 12, color: '#fff' }}>{translations['seller_' + msg.id] || '⏳ Tłumaczę...'}</div>
                              <button onClick={() => setShowTranslation(prev => ({...prev, ['seller_' + msg.id]: false}))}
                                style={{ fontSize: 11, color: '#bfdbfe', background: 'none', border: 'none', cursor: 'pointer' }}>Ukryj</button>
                            </div>
                          ) : (
                            <button onClick={async () => {
                              setShowTranslation(prev => ({...prev, ['seller_' + msg.id]: true}));
                              if (!translations['seller_' + msg.id]) {
                                const res = await axios.post(`${API}/ai/translate-to-polish`, { text: msg.text, source_lang: issueLang });
                                setTranslations(prev => ({...prev, ['seller_' + msg.id]: res.data.translation}));
                              }
                            }} style={{ fontSize: 11, color: '#bfdbfe', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                              🇵🇱 Pokaż po polsku
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Załączniki */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        {msg.attachments.map((att: any, i: number) => (
                          <div key={i}>
                            {(att.mimeType && att.mimeType.startsWith('image/')) || att.fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                              <a href={att.proxy_url ? `/api${att.proxy_url}&token=${getAuthToken()}` : att.url} target="_blank" rel="noreferrer">
                                <img src={att.proxy_url ? `/api${att.proxy_url}&token=${getAuthToken()}` : att.url}
                                  alt={att.fileName}
                                  style={{ maxWidth: 200, maxHeight: 200, borderRadius: 6,
                                    border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', display: 'block', marginTop: 4 }}
                                  onError={(e) => { (e.target as HTMLImageElement).style.display='none'; }} />
                              </a>
                            ) : (
                              <a href={att.proxy_url ? `/api${att.proxy_url}&token=${getAuthToken()}` : att.url} target="_blank" rel="noreferrer"
                                style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, marginTop: 4,
                                  background: 'rgba(0,0,0,0.08)', color: 'inherit',
                                  textDecoration: 'none', display: 'inline-block' }}>
                                📎 {att.fileName || 'Załącznik'}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                  </div>
                );
              })}
              {lockInfo && (
                <div style={{ margin: '10px 0', padding: '10px 14px', background: '#fef9c3',
                  border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                  ⚠️ {lockInfo} – możesz przeglądać ale uważaj na konflikty
                </div>
              )}
              {!loadingChat && chat.length >= 100 && !!issueMeta?.messagesCount && issueMeta.messagesCount > chat.length && (
            <div style={{ marginTop: 12, marginBottom: 4, padding: '10px 12px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠️ Widzisz tylko {chat.length} z {issueMeta.messagesCount} wiadomości</div>
              <div>Allegro API udostępnia tylko 100 najstarszych wiadomości w sprawie (znany błąd Allegro, bez terminu poprawki). Najnowsze wiadomości, w tym odpowiedzi doradcy Allegro i Twoje, są widoczne tylko w Sales Center.</div>
              {selected?.type === 'CLAIM' && (
                <a href={`https://salescenter.allegro.com/claims/${selected.id}`} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-block', marginTop: 6, color: '#92400e', fontWeight: 600 }}>Otwórz tę reklamację w Sales Center ↗</a>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', padding: '12px 18px', background: '#fff' }}>
              {selected.currentState?.chatActive === false ? (
                <div style={{ padding: '10px 14px', background: '#f3f4f6', borderRadius: 6, fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
                  Ta dyskusja jest zamknięta – nie można wysyłać wiadomości
                </div>
              ) : (
                <>
                  {sent && (
                    <div style={{ padding: '7px 12px', borderRadius: 6, fontSize: 12, marginBottom: 8,
                      background: sent.includes('Błąd') ? '#fef2f2' : '#f0fdf4',
                      color: sent.includes('Błąd') ? '#dc2626' : '#15803d' }}>
                      {sent}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#7B79C9', marginBottom: 6, padding: '4px 8px',
                    background: '#EEEDFE', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>🌐 Klient otrzyma odpowiedź w języku:</span>
                    <select value={issueLang} onChange={e => { setIssueLang(e.target.value); setReplyTranslation(''); }} style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #C4C2F5', borderRadius: 6, color: '#3C3489', background: '#fff', cursor: 'pointer' }}>
                      {[{v:'pl',l:'🇵🇱 Polski'},{v:'en',l:'🇬🇧 Angielski'},{v:'de',l:'🇩🇪 Niemiecki'},{v:'cs',l:'🇨🇿 Czeski'},{v:'sk',l:'🇸🇰 Słowacki'},{v:'hu',l:'🇭🇺 Węgierski'},{v:'ro',l:'🇷🇴 Rumuński'},{v:'uk',l:'🇺🇦 Ukraiński'},{v:'ru',l:'🇷🇺 Rosyjski'},{v:'fr',l:'🇫🇷 Francuski'},{v:'it',l:'🇮🇹 Włoski'}].map(o => (
                        <option key={o.v} value={o.v}>{o.l}</option>
                      ))}
                    </select>
                  </div>
                  {replyTranslation && (
                    <div style={{ fontSize: 12, color: '#3C3489', marginBottom: 8, padding: '8px 10px',
                      background: '#EEEDFE', borderRadius: 6, lineHeight: 1.5 }}>
                      <div style={{ fontSize: 11, marginBottom: 4 }}>Podgląd {issueLang.toUpperCase()}:</div>
                      {replyTranslation}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <DictateButton disabled={aiLoading} onText={(t: string) => { const cur = reply || ''; handleReplyChange(cur ? cur + ' ' + t : t, (v: any) => { setReply(v); setReplyTranslation(''); setReplyIsManual(true); }); }} />
                  <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                  <textarea ref={replyTextareaRef} value={reply} onChange={e => { handleReplyChange(e.target.value, (v) => { setReply(v); setReplyTranslation(''); setReplyIsManual(true); }); }} onKeyDown={e => { if (e.key === 'Escape') setShowReplySuggestions(false); }} onInput={(e:any)=>{const t=e.currentTarget;t.style.height='auto';t.style.height=Math.min(t.scrollHeight,400)+'px';}}
                    placeholder="Wpisz wiadomość (dowolny język – klient dostanie w wybranym wyżej)..."
                    style={{ width: '100%', minHeight: 160, padding: '9px 12px', border: '1px solid #d1d5db',
                      borderRadius: 6, fontSize: 13, fontFamily: 'system-ui', resize: 'vertical',
                      outline: 'none', boxSizing: 'border-box' }} />
                  {showReplySuggestions && <ShortcutSuggestions suggestions={replySuggestions} onSelect={s => applyReplyShortcut(s, reply, (v) => { setReply(v); setReplyTranslation(''); setReplyIsManual(true); })} onClose={() => setShowReplySuggestions(false)} />}
                  </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
	                      <label style={{ padding: '7px 12px', background: '#f3f4f6', border: '1px solid #d1d5db',
	                        borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap' }}>
	                        📎 {uploadingFile ? 'Uploading...' : 'Załącz plik'}
	                        <input type="file" style={{ display: 'none' }}
	                          accept="image/png,image/jpeg,image/gif,image/bmp,image/tiff,application/pdf"
	                          onChange={e => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }}
	                          disabled={uploadingFile} />
	                      </label>
	                      {attachments.map((a, idx) => (
	                        <span key={idx} style={{ padding: '3px 8px', background: '#dbeafe', borderRadius: 6,
	                          fontSize: 12, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
	                          📄 {a.filename}
	                          <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
	                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14, padding: 0 }}>✕</button>
	                        </span>
	                      ))}
                    {selected && isAccountExpired(selected.account_name || '') && (
                      <div style={{ padding: '6px 10px', background: '#fef2f2', borderRadius: 6, fontSize: 12,
                        color: '#dc2626', marginBottom: 8 }}>
                        ⚠️ Brak połączenia z {selected.account_name} – nie można wysłać
                      </div>
                    )}
                    {showOfferPicker && (
                      <OfferPicker
                        defaultAccountId={selected?.account_id}
                        onClose={() => setShowOfferPicker(false)}
                        onInsertLink={(link) => {
                          const ta = replyTextareaRef.current;
                          if (ta) {
                            const start = ta.selectionStart;
                            const end = ta.selectionEnd;
                            const newVal = reply.slice(0, start) + link + reply.slice(end);
                            setReply(newVal);
                            setReplyIsManual(true);
                            setTimeout(() => { ta.focus(); ta.setSelectionRange(start + link.length, start + link.length); }, 0);
                          } else {
                            setReply(prev => prev + link);
                          }
                          setShowOfferPicker(false);
                        }}
                      />
                    )}
                    <button onClick={() => setShowOfferPicker(v => !v)}
                      style={{ padding: '7px 14px', background: showOfferPicker ? '#1d4ed8' : '#f0f9ff',
                        color: showOfferPicker ? '#fff' : '#0369a1', border: '1px solid #bae6fd',
                        borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                      🏷️ Oferta
                    </button>
                    <button onClick={() => sendMessage(false)} disabled={sending || !reply.trim() || isAccountExpired(selected?.account_name || '')}
                      style={{ padding: '7px 16px', background: !reply.trim() ? '#9ca3af' : '#1d4ed8',
                        color: '#fff', border: 'none', borderRadius: 6, fontSize: 13,
                        cursor: !reply.trim() ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                      {sending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                    </button>
                    <button onClick={() => sendMessage(true)} disabled={sending || !reply.trim() || isAccountExpired(selected?.account_name || '')}
                      style={{ padding: '7px 16px', background: !reply.trim() ? '#f9fafb' : '#f0fdf4',
                        color: !reply.trim() ? '#9ca3af' : '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13,
                        cursor: !reply.trim() ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                      {sending ? 'Wysyłanie...' : 'Wyślij i ucz'}
                    </button>
                    <button onClick={generateAI} disabled={aiLoading}
                      style={{ padding: '7px 14px', background: '#f0fdf4', color: '#15803d',
                        border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                      {aiLoading ? '⏳ Generuję...' : '✦ Generuj AI'}
                    </button>
                    {issueLang !== 'pl' && reply.trim() && (
                      <button onClick={async () => {
                        setTranslatingReply(true);
                        const res = await axios.post(`${API}/ai/translate-from-polish`, { text: reply, target_lang: issueLang });
                        setReplyTranslation(res.data.translation);
                        setTranslatingReply(false);
                      }} disabled={translatingReply}
                        style={{ padding: '7px 12px', background: '#EEEDFE', color: '#3C3489',
                          border: '1px solid #C4C2F5', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                        {translatingReply ? '⏳' : `🌐 Podgląd ${issueLang.toUpperCase()}`}
                      </button>
                    )}
                    <button onClick={() => { setReply(''); setReplyTranslation(''); }}
                      style={{ padding: '7px 14px', background: 'transparent', color: '#6b7280',
                        border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                      Wyczyść
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
      {isMobile && (
        <div style={{ display: 'flex', background: '#1d4ed8', padding: '8px 0', paddingBottom: 'env(safe-area-inset-bottom)', flexShrink: 0 }}>
          {mobileView === 'detail' ? (
            <button onClick={() => setMobileView('list')}
              style={{ flex: 1, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11, borderRadius: 8, margin: '0 8px' }}>
              <div style={{ fontSize: 20 }}>←</div>
              <div>Wróć</div>
            </button>
          ) : (
            <>
              <button onClick={onBack}
                style={{ flex: 1, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11 }}>
                <div style={{ fontSize: 20 }}>✉️</div>
                <div>Skrzynka</div>
              </button>
              <button onClick={() => fetchIssues(0)}
                style={{ flex: 1, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11, borderRadius: 8 }}>
                <div style={{ fontSize: 20 }}>↻</div>
                <div>Odśwież</div>
              </button>
            </>
          )}
        </div>
      )}
    </div>
    </div>
    </>
  );
}
